import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Lightning } from "@phosphor-icons/react";
import Visualizer from "@/components/Visualizer";

export default function AuthPage() {
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "login" ? "login" : "signup";
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, signup } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (mode === "signup" && !agreed) {
      setErr("You must agree to the Terms of Service to continue");
      return;
    }
    setBusy(true);
    try {
      let u;
      if (mode === "signup") {
        u = await signup(email, password, name, agreed);
      } else {
        u = await login(email, password);
      }
      const redirect = params.get("redirect");
      if (redirect) navigate(redirect);
      else if (u?.is_admin) navigate("/admin");
      else navigate("/dashboard");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      {/* Left visual */}
      <div className="hidden lg:flex relative overflow-hidden border-r border-zinc-900">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="absolute inset-0 scanlines opacity-50" />
        <div className="absolute inset-0"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1774993640958-45a668d057bd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')",
            backgroundSize: "cover",
            mixBlendMode: "screen",
            opacity: 0.35,
          }} />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/80" />
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <div className="text-[10px] tracking-[0.4em] uppercase text-brand-red">◢ ACCESS THE LAB</div>
          <div>
            <h2 className="font-display font-black uppercase tracking-tighter text-6xl xl:text-8xl leading-[0.85]">
              SOUND<br />
              <span className="text-brand-red neon-red">IS</span><br />
              CURRENCY.
            </h2>
            <p className="text-ink-mute text-sm mt-6 max-w-md">
              Your producer identity, your presets, your subscription — synced across every session.
            </p>
            <div className="mt-10 border border-zinc-900 bg-black/40">
              <Visualizer height={140} style="wave" />
            </div>
          </div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-ink-dim flex items-center gap-3">
            <span className="w-1.5 h-1.5 bg-brand-green rounded-full animate-pulse" />
            12,847 PRODUCERS LOGGED IN TONIGHT
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <form
          onSubmit={submit}
          data-testid="auth-form"
          className="w-full max-w-md border border-zinc-800 bg-bg-surface p-8 grain"
        >
          <div className="flex items-center gap-2 mb-8">
            <Lightning size={20} weight="fill" className="text-brand-red" />
            <span className="text-[10px] tracking-[0.4em] uppercase text-ink-dim">
              {mode === "signup" ? "CREATE PRODUCER ACCOUNT" : "PRODUCER LOGIN"}
            </span>
          </div>

          <h1 className="font-display font-black uppercase tracking-tighter text-4xl">
            {mode === "signup" ? "JOIN" : "WELCOME"} <br />
            <span className="text-brand-red">THE LAB.</span>
          </h1>

          <div className="mt-8 space-y-4">
            {mode === "signup" && (
              <Field label="Producer name" value={name} onChange={setName} placeholder="DJ X-808" testid="auth-name" required />
            )}
            <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="producer@studio.com" testid="auth-email" required />
            <Field label="Password" type="password" value={password} onChange={setPassword} placeholder="••••••" testid="auth-password" required minLength={6} />
          </div>

          {mode === "signup" && (
            <label className="mt-4 flex items-start gap-2.5 cursor-pointer" data-testid="auth-terms-row">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                data-testid="auth-terms-checkbox"
                className="mt-0.5 w-4 h-4 accent-brand-red"
              />
              <span className="text-[11px] tracking-[0.05em] text-ink-mute leading-relaxed">
                I AGREE to the{" "}
                <Link to="/terms" target="_blank" data-testid="auth-terms-link" className="text-brand-red underline hover:text-white">
                  Terms of Service
                </Link>
                {" "}— I understand the plugin is licensed (not sold), that my subscription renews monthly via Square,
                and that BEAT-LAB may suspend my account if I violate the anti-piracy clause.
              </span>
            </label>
          )}

          {err && (
            <div className="mt-4 text-xs text-brand-red border border-brand-red/40 bg-brand-red/10 px-3 py-2" data-testid="auth-error">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="auth-submit"
            className="mt-6 w-full bg-brand-red text-black text-xs font-bold tracking-[0.25em] uppercase py-4 hover:bg-white transition-colors disabled:opacity-50 btn-glitch"
          >
            {busy ? "..." : mode === "signup" ? "CREATE ACCOUNT ▸" : "LOG IN ▸"}
          </button>

          <button
            type="button"
            data-testid="auth-toggle"
            onClick={() => setMode(mode === "signup" ? "login" : "signup")}
            className="mt-6 w-full text-[11px] tracking-[0.2em] uppercase text-ink-mute hover:text-brand-red transition-colors"
          >
            {mode === "signup" ? "Already in the lab? Log in" : "New here? Create account"}
          </button>

          <Link to="/" className="mt-3 block text-center text-[10px] tracking-[0.3em] uppercase text-ink-dim hover:text-white">
            ← BACK
          </Link>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid, ...rest }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.3em] uppercase text-ink-dim">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        {...rest}
        className="mt-1.5 w-full bg-black border border-zinc-800 px-3 py-3 text-sm text-white placeholder:text-ink-dim font-mono outline-none focus:border-brand-red transition-colors"
      />
    </label>
  );
}
